import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Target, Award, Users, Lightbulb, CheckCircle, Zap, Building2, Hammer, ClipboardList, MapPin, FileSignature, Ruler, Calculator, FileCheck, Calendar, Search, Headphones, KeyRound, Wallet, ShieldCheck } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingBubbles from '../components/FloatingBubbles';
import TeamSection from '../components/TeamSection';

const AboutUsPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();

  const values = [
    {
      icon: Award,
      title: language === 'fr' ? 'Excellence' : 'Excellence',
      description: language === 'fr' 
        ? 'Nous visons la perfection dans chaque projet, de la conception à la réalisation finale.'
        : 'We aim for perfection in every project, from design to final completion.'
    },
    {
      icon: Users,
      title: language === 'fr' ? 'Engagement Client' : 'Client Commitment',
      description: language === 'fr'
        ? 'Votre satisfaction est notre priorité. Nous vous accompagnons à chaque étape de votre projet.'
        : 'Your satisfaction is our priority. We support you at every stage of your project.'
    },
    {
      icon: Lightbulb,
      title: language === 'fr' ? 'Innovation' : 'Innovation',
      description: language === 'fr'
        ? 'Nous intégrons les dernières technologies pour des solutions durables et performantes.'
        : 'We integrate the latest technologies for sustainable and efficient solutions.'
    },
    {
      icon: Target,
      title: language === 'fr' ? 'Qualité' : 'Quality',
      description: language === 'fr'
        ? 'Des matériaux de premier choix et un savoir-faire reconnu pour garantir la pérennité de vos bâtiments.'
        : 'Premium materials and recognized expertise to ensure the longevity of your buildings.'
    }
  ];

  const expertise = [
    {
      icon: Building2,
      title: language === 'fr' ? 'Construction Neuve' : 'New Construction',
      description: language === 'fr'
        ? 'Maisons individuelles et immeubles résidentiels. De la conception architecturale à la livraison clé en main, nous réalisons vos projets de A à Z.'
        : 'Individual houses and residential buildings. From architectural design to turnkey delivery, we realize your projects from A to Z.'
    },
    {
      icon: Zap,
      title: language === 'fr' ? 'Installations Photovoltaïques' : 'Photovoltaic Installations',
      description: language === 'fr'
        ? 'Étude énergétique, dimensionnement et installation de panneaux solaires. Optimisez votre consommation et réduisez vos factures énergétiques.'
        : 'Energy study, sizing and installation of solar panels. Optimize your consumption and reduce your energy bills.'
    },
    {
      icon: Hammer,
      title: language === 'fr' ? 'Rénovation & Réhabilitation' : 'Renovation & Rehabilitation',
      description: language === 'fr'
        ? 'Modernisation, extension et restauration de bâtiments existants. Nous redonnons vie à vos espaces tout en respectant leur caractère original.'
        : 'Modernization, extension and restoration of existing buildings. We breathe new life into your spaces while respecting their original character.'
    }
  ];

  const stats = [
    { value: '18+', label: language === 'fr' ? 'Années d\'expérience' : 'Years of experience' },
    { value: '50+', label: language === 'fr' ? 'Projets réalisés' : 'Completed projects' },
    { value: '20+', label: language === 'fr' ? 'Professionnels qualifiés' : 'Qualified professionals' },
    { value: '98%', label: language === 'fr' ? 'Clients satisfaits' : 'Satisfied clients' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="pt-[240px]">
        {/* Hero Section */}
        <section className="relative h-[380px] md:h-[460px] overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage:
                "url('https://customer-assets-wrfwihn1.emergentagent.net/job_construction-global/artifacts/7s6tyk8w_WhatsApp%20Image%202026-09-08%20at%2000.23.37.webp')",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/25" />
          <div className="relative max-w-6xl mx-auto px-6 h-full flex flex-col justify-center">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-white/90 hover:text-white transition-colors mb-4 self-start"
            >
              <ArrowLeft size={20} />
              <span className="font-semibold">{language === 'fr' ? 'Retour à l\'accueil' : 'Back to home'}</span>
            </button>
            <span className="inline-block text-orange-400 font-semibold tracking-widest text-sm mb-3">
              {language === 'fr' ? 'GCI · ÉQUIPE' : 'GCI · TEAM'}
            </span>
            <h1 className="text-white text-3xl sm:text-4xl md:text-5xl font-bold leading-tight max-w-3xl">
              {language === 'fr' ? 'Qui sommes-nous ?' : 'About Us'}
            </h1>
            <div className="mt-4 h-[3px] w-20 bg-orange-500 rounded-full" />
            <p className="text-white/85 mt-4 max-w-2xl text-base md:text-lg">
              {language === 'fr'
                ? 'GLOBAL CONSTRUCTION & INGEGNERIE SARL, votre partenaire de confiance pour tous vos projets de construction, rénovation et installation photovoltaïque.'
                : 'GLOBAL CONSTRUCTION & ENGINEERING SARL, your trusted partner for all your construction, renovation and photovoltaic installation projects.'}
            </p>
          </div>
        </section>

        {/* Notre Équipe - EN PREMIER */}
        <TeamSection />

        {/* Notre Histoire */}
        <section className="py-12 bg-white relative overflow-hidden">
          <FloatingBubbles count={12} />
          <div className="max-w-[1800px] mx-auto px-6 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-6xl font-bold mb-6" style={{ color: '#702963' }}>
                  {language === 'fr' ? 'Notre Histoire' : 'Our Story'}
                </h2>
                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    {language === 'fr'
                      ? 'Depuis plus de 18 ans, GLOBAL CONSTRUCTION & INGEGNERIE SARL s\'est imposée comme un acteur majeur dans le secteur de la construction en République Démocratique du Congo. Forte d\'une équipe de plus de 50 professionnels qualifiés, notre entreprise accompagne particuliers et professionnels dans la concrétisation de leurs projets immobiliers.'
                      : 'For over 18 years, GLOBAL CONSTRUCTION & ENGINEERING SARL has established itself as a major player in the construction sector in the Democratic Republic of Congo. With a team of more than 50 qualified professionals, our company supports individuals and professionals in bringing their real estate projects to life.'}
                  </p>
                  <p>
                    {language === 'fr'
                      ? 'Notre expertise couvre l\'ensemble du cycle de vie d\'un bâtiment : de la conception initiale à la maintenance, en passant par la construction et la rénovation. Nous nous distinguons par notre capacité à intégrer les technologies les plus modernes, notamment dans le domaine des énergies renouvelables avec nos installations photovoltaïques.'
                      : 'Our expertise covers the entire life cycle of a building: from initial design to maintenance, including construction and renovation. We stand out for our ability to integrate the most modern technologies, particularly in the field of renewable energies with our photovoltaic installations.'}
                  </p>
                  <p>
                    {language === 'fr'
                      ? 'Guidés par notre devise "L\'excellence est ma loi", nous mettons un point d\'honneur à respecter les délais, maîtriser les budgets et garantir la qualité de nos réalisations. Chaque projet est pour nous l\'occasion de démontrer notre savoir-faire et notre engagement envers nos clients.'
                      : 'Guided by our motto "Excellence is my law", we make it a point of honor to respect deadlines, control budgets and guarantee the quality of our achievements. Each project is an opportunity for us to demonstrate our expertise and commitment to our clients.'}
                  </p>
                </div>
              </div>
              <div className="relative">
                <video
                  src="https://customer-assets.emergentagent.com/job_construction-global/artifacts/7k223r2l_WhatsApp%20Video%202026-05-07%20at%2011.45.43.mp4"
                  className="rounded-2xl shadow-2xl w-full h-auto object-cover"
                  autoPlay
                  loop
                  muted
                  playsInline
                  data-testid="histoire-video"
                />
                <div className="absolute -bottom-6 -left-6 bg-orange-500 text-white p-8 rounded-xl shadow-xl">
                  <div className="text-6xl font-bold mb-2">18+</div>
                  <div className="text-2xl font-semibold">
                    {language === 'fr' ? 'Années d\'excellence' : 'Years of excellence'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Nos Prestations */}
        <section className="py-16 bg-white" data-testid="prestations-section">
          <div className="max-w-7xl mx-auto px-6">
            {/* Header */}
            <div className="text-center mb-12">
              <span className="inline-block text-orange-500 font-semibold tracking-widest text-sm mb-3">
                {language === 'fr' ? 'GLOBAL CONSTRUCTION & INGÉNIERIE' : 'GLOBAL CONSTRUCTION & INGÉNIERIE'}
              </span>
              <h2 className="text-3xl md:text-5xl font-bold mb-4" style={{ color: '#702963' }} data-testid="prestations-title">
                {language === 'fr' ? 'Nos prestations' : 'Our services'}
              </h2>
              <div className="mx-auto h-[3px] w-20 bg-orange-500 rounded-full" />
              <p className="mt-6 text-base md:text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed">
                {language === 'fr'
                  ? 'Concrétisez vos projets de construction et de rénovation à Kinshasa avec un maître d\u2019œuvre attentif, indépendant et expérimenté. Nous organisons votre espace selon vos besoins et votre budget — sans plan type imposé.'
                  : 'Bring your construction and renovation projects in Kinshasa to life with an attentive, independent and experienced project manager. We design your space around your needs and budget — no off-the-shelf plans.'}
              </p>
            </div>

            {/* 3 commitments */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
              {[
                {
                  Icon: Headphones,
                  title: language === 'fr' ? 'À votre écoute' : 'We listen',
                  text: language === 'fr'
                    ? 'Nous prenons le temps de comprendre vos attentes, vos contraintes et votre mode de vie avant toute proposition technique.'
                    : 'We take the time to understand your expectations, constraints and lifestyle before any technical proposal.',
                },
                {
                  Icon: Wallet,
                  title: language === 'fr' ? 'Projet & budget alignés' : 'Project & budget aligned',
                  text: language === 'fr'
                    ? 'Nous calons chaque décision technique sur votre enveloppe budgétaire, sans surprise ni dérive en cours de chantier.'
                    : 'Every technical decision is aligned with your budget envelope, with no surprises or drift during the works.',
                },
                {
                  Icon: CheckCircle,
                  title: language === 'fr' ? 'Premier contact gratuit' : 'Free first meeting',
                  text: language === 'fr'
                    ? 'Le rendez-vous de cadrage initial est offert : nous étudions la faisabilité de votre projet sans engagement.'
                    : 'The initial scoping meeting is free of charge: we assess the feasibility of your project with no commitment.',
                },
              ].map((c, i) => {
                const Icon = c.Icon;
                return (
                  <div
                    key={i}
                    className="bg-gray-50 p-7 rounded-sm border-l-4 border-orange-500 hover:shadow-lg transition-all duration-300"
                    data-testid={`commitment-card-${i}`}
                  >
                    <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full text-orange-600 mb-4 shadow">
                      <Icon className="w-6 h-6" strokeWidth={2} />
                    </div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">{c.title}</h3>
                    <p className="text-sm md:text-base text-gray-700 leading-relaxed">{c.text}</p>
                  </div>
                );
              })}
            </div>

            {/* Intervention stages with image */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center mb-16">
              <div className="relative order-2 lg:order-1">
                <img
                  src="https://customer-assets-wrfwihn1.emergentagent.net/job_construction-global/artifacts/s577qgv1_Capture%20d%27%C3%A9cran%202026-09-07%20222951.png"
                  alt={language === 'fr' ? 'Plans de construction' : 'Construction plans'}
                  className="rounded-sm shadow-2xl w-full h-auto object-cover"
                  loading="lazy"
                />
                <div className="absolute -top-4 -left-4 hidden md:block w-24 h-24 border-4 border-orange-500 rounded-sm -z-10" />
              </div>
              <div className="order-1 lg:order-2">
                <h3 className="text-2xl md:text-4xl font-bold mb-5" style={{ color: '#702963' }}>
                  {language === 'fr'
                    ? 'Présents à chaque étape de votre projet'
                    : 'Present at every stage of your project'}
                </h3>
                <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-6">
                  {language === 'fr'
                    ? 'Le maître d\u2019œuvre n\u2019est pas qu\u2019un dessinateur de plans : il pilote votre projet de la première intuition à la remise des clés. Voici les missions que nous prenons en charge.'
                    : 'A project manager is not just a draughtsperson: we drive your project from the first idea to the handover. Here are the missions we take on.'}
                </p>
                <ul className="space-y-3">
                  {[
                    [MapPin, language === 'fr' ? 'Recherche et choix du terrain à bâtir' : 'Search and selection of the building plot'],
                    [Ruler, language === 'fr' ? 'Conception, dessin du projet et estimation des coûts' : 'Design, drawings and cost estimation'],
                    [FileCheck, language === 'fr' ? 'Démarches administratives et permis de bâtir' : 'Administrative procedures and building permit'],
                    [Calendar, language === 'fr' ? 'Contrôle général des travaux et respect du calendrier' : 'Overall works supervision and schedule control'],
                    [Calculator, language === 'fr' ? 'Maîtrise du budget et suivi des facturations' : 'Budget control and invoicing follow-up'],
                  ].map(([Icon, label], i) => (
                    <li key={i} className="flex items-start gap-3 text-gray-800">
                      <span className="w-9 h-9 rounded-full bg-[#702963]/10 text-[#702963] flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5" strokeWidth={2} />
                      </span>
                      <span className="text-base md:text-lg pt-1.5">{label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Engagement contractuel */}
            <div className="bg-[#702963] text-white p-8 md:p-12 rounded-sm shadow-2xl mb-16" data-testid="engagement-block">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
                <div className="lg:col-span-2">
                  <div className="flex items-center gap-3 mb-3">
                    <FileSignature className="w-7 h-7 text-orange-400" />
                    <span className="uppercase tracking-widest text-orange-300 text-xs font-semibold">
                      {language === 'fr' ? 'Notre engagement' : 'Our commitment'}
                    </span>
                  </div>
                  <h3 className="text-xl md:text-3xl font-bold mb-4">
                    {language === 'fr'
                      ? 'Un contrat clair, des honoraires transparents'
                      : 'A clear contract, transparent fees'}
                  </h3>
                  <p className="text-white/90 text-base md:text-lg leading-relaxed">
                    {language === 'fr'
                      ? 'Nous formalisons chaque mission par un contrat écrit signé avec vous. Nos honoraires sont calculés en fonction de l\u2019étendue des prestations confiées et nous fournissons systématiquement une attestation d\u2019assurance couvrant notre responsabilité professionnelle.'
                      : 'Every mission is formalised by a written contract signed with you. Our fees depend on the scope of services entrusted to us and we systematically provide a certificate of professional liability insurance.'}
                  </p>
                </div>
                <div className="flex justify-center lg:justify-end">
                  <ShieldCheck className="w-32 h-32 text-orange-400/80" strokeWidth={1.4} />
                </div>
              </div>
            </div>

            {/* Services & compétences grid */}
            <div className="text-center mb-10">
              <h3 className="text-2xl md:text-4xl font-bold mb-3" style={{ color: '#702963' }}>
                {language === 'fr' ? 'Nos services & compétences' : 'Our services & skills'}
              </h3>
              <div className="mx-auto h-[3px] w-16 bg-orange-500 rounded-full" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5" data-testid="services-grid">
              {[
                language === 'fr' ? 'Bureau d\u2019études et maîtrise d\u2019œuvre' : 'Engineering office and project management',
                language === 'fr' ? 'Étude de plans personnalisés en neuf et rénovation' : 'Custom drawings for new builds and renovation',
                language === 'fr' ? 'Architecture contemporaine et traditionnelle' : 'Contemporary and traditional architecture',
                language === 'fr' ? 'Conception adaptée au climat tropical de Kinshasa' : 'Design adapted to the Kinshasa tropical climate',
                language === 'fr' ? 'Mission partielle ou complète, selon vos besoins' : 'Partial or full mission, tailored to your needs',
                language === 'fr' ? 'Avant-projet sommaire et avant-projet détaillé' : 'Preliminary and detailed design studies',
                language === 'fr' ? 'Études thermiques et performance énergétique' : 'Thermal and energy-performance studies',
                language === 'fr' ? 'Demande de permis de bâtir auprès des autorités' : 'Building-permit applications with the authorities',
                language === 'fr' ? 'Appels d\u2019offre et consultation des entrepreneurs locaux' : 'Tendering and consultation of local contractors',
                language === 'fr' ? 'Conception générale et coordination des corps de métier' : 'General design and trade coordination',
                language === 'fr' ? 'Ordonnancement, pilotage et coordination de chantier (OPC)' : 'Scheduling, supervision and site coordination (OPC)',
                language === 'fr' ? 'Planification détaillée des travaux' : 'Detailed works planning',
                language === 'fr' ? 'Suivi de la facturation et maîtrise des coûts' : 'Invoicing follow-up and cost control',
                language === 'fr' ? 'Assistance aux opérations de réception' : 'Assistance during handover operations',
                language === 'fr' ? 'Remise des clés et accompagnement post-livraison' : 'Keys handover and post-delivery support',
              ].map((label, i) => (
                <div
                  key={i}
                  className="flex items-start gap-3 bg-gray-50 hover:bg-orange-50 border border-gray-200 hover:border-orange-300 p-4 rounded-sm transition-all duration-300"
                  data-testid={`service-item-${i}`}
                >
                  <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                  <span className="text-sm md:text-base text-gray-800 leading-snug">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Nos Valeurs */}
        <section className="py-12 bg-gray-50">
          <div className="max-w-[1800px] mx-auto px-6">
            <div className="text-center mb-10">
              <h2 className="text-6xl font-bold mb-4" style={{ color: '#702963' }}>
                {language === 'fr' ? 'Nos Valeurs' : 'Our Values'}
              </h2>
              <p className="text-2xl text-gray-600">
                {language === 'fr'
                  ? 'Les principes qui guident notre action au quotidien'
                  : 'The principles that guide our daily action'}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, index) => {
                const Icon = value.icon;
                return (
                  <div
                    key={index}
                    className="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
                  >
                    <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center mb-6">
                      <Icon size={32} className="text-white" />
                    </div>
                    <h3 className="text-2xl font-bold mb-4 text-gray-800">{value.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{value.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Notre Expertise */}
        <section className="py-12 bg-white relative overflow-hidden">
          <FloatingBubbles count={10} />
          <div className="max-w-[1800px] mx-auto px-6 relative z-10">
            <div className="text-center mb-10">
              <h2 className="text-6xl font-bold mb-4" style={{ color: '#702963' }}>
                {language === 'fr' ? 'Notre Expertise' : 'Our Expertise'}
              </h2>
              <p className="text-2xl text-gray-600">
                {language === 'fr'
                  ? 'Des solutions complètes pour tous vos besoins en construction'
                  : 'Complete solutions for all your construction needs'}
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {expertise.map((item, index) => {
                const Icon = item.icon;
                return (
                  <div
                    key={index}
                    className="group bg-gray-50 p-8 rounded-xl hover:bg-white hover:shadow-2xl transition-all duration-300"
                  >
                    <div className="w-20 h-20 bg-gradient-to-br from-purple-900 to-purple-700 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                      <Icon size={36} className="text-white" />
                    </div>
                    <h3 className="text-2xl font-bold mb-4" style={{ color: '#702963' }}>
                      {item.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">{item.description}</p>
                    <div className="mt-6 flex items-center gap-2 text-orange-500 font-semibold group-hover:gap-4 transition-all">
                      {language === 'fr' ? 'En savoir plus' : 'Learn more'}
                      <CheckCircle size={20} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Statistiques */}
        <section 
          className="py-12 relative overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #702963 0%, #FF8C00 100%)' }}
        >
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full -translate-x-1/2 -translate-y-1/2" />
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/2 translate-y-1/2" />
          </div>
          <div className="max-w-[1800px] mx-auto px-6 relative z-10">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className="text-center transform hover:scale-110 transition-transform duration-300"
                >
                  <div className="text-6xl font-bold text-white mb-4">{stat.value}</div>
                  <div className="text-2xl text-white/90 font-semibold">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Notre Engagement */}
        <section className="py-12 bg-gray-50">
          <div className="max-w-[1800px] mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div className="relative order-2 lg:order-1">
                <img
                  src="https://customer-assets-wrfwihn1.emergentagent.net/job_construction-global/artifacts/cc4cfeax_ef273cbc-7ad2-49a4-b41f-9d68d2ca634b.png"
                  alt="Équipe GCI"
                  className="rounded-2xl shadow-2xl w-full h-auto object-cover"
                />
              </div>
              <div className="order-1 lg:order-2">
                <h2 className="text-6xl font-bold mb-6" style={{ color: '#702963' }}>
                  {language === 'fr' ? 'Notre Engagement' : 'Our Commitment'}
                </h2>
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <CheckCircle size={28} className="text-orange-500 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-2xl font-bold mb-2 text-gray-800">
                        {language === 'fr' ? 'Accompagnement Personnalisé' : 'Personalized Support'}
                      </h3>
                      <p className="text-gray-600">
                        {language === 'fr'
                          ? 'Un interlocuteur dédié pour chaque projet, disponible à chaque étape de votre construction.'
                          : 'A dedicated contact for each project, available at every stage of your construction.'}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <CheckCircle size={28} className="text-orange-500 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-2xl font-bold mb-2 text-gray-800">
                        {language === 'fr' ? 'Respect des Délais' : 'Deadline Compliance'}
                      </h3>
                      <p className="text-gray-600">
                        {language === 'fr'
                          ? 'Une planification rigoureuse et un suivi quotidien pour garantir la livraison à temps de vos projets.'
                          : 'Rigorous planning and daily monitoring to ensure on-time delivery of your projects.'}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <CheckCircle size={28} className="text-orange-500 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-2xl font-bold mb-2 text-gray-800">
                        {language === 'fr' ? 'Qualité Garantie' : 'Quality Guaranteed'}
                      </h3>
                      <p className="text-gray-600">
                        {language === 'fr'
                          ? 'Matériaux certifiés, artisans qualifiés et contrôle qualité à chaque étape de la réalisation.'
                          : 'Certified materials, qualified craftsmen and quality control at every stage of execution.'}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <CheckCircle size={28} className="text-orange-500 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-2xl font-bold mb-2 text-gray-800">
                        {language === 'fr' ? 'Solutions Durables' : 'Sustainable Solutions'}
                      </h3>
                      <p className="text-gray-600">
                        {language === 'fr'
                          ? 'Intégration des énergies renouvelables et techniques de construction écologiques pour un avenir durable.'
                          : 'Integration of renewable energies and ecological construction techniques for a sustainable future.'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-12 bg-white relative overflow-hidden">
          <FloatingBubbles count={15} />
          <div className="max-w-6xl mx-auto px-6 text-center relative z-10">
            <h2 className="text-6xl font-bold mb-6" style={{ color: '#702963' }}>
              {language === 'fr' ? 'Prêt à démarrer votre projet ?' : 'Ready to start your project?'}
            </h2>
            <p className="text-2xl text-gray-600 mb-6">
              {language === 'fr'
                ? 'Contactez-nous dès aujourd\'hui pour discuter de vos besoins et obtenir un devis personnalisé.'
                : 'Contact us today to discuss your needs and get a personalized quote.'}
            </p>
            <button
              onClick={() => navigate('/votre-projet')}
              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-12 py-4 rounded-xl font-bold text-2xl transition-all duration-300 transform hover:scale-105 shadow-xl"
            >
              {language === 'fr' ? 'Parlez-nous de votre projet' : 'Tell us about your project'}
            </button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default AboutUsPage;
